/* eslint-disable no-unused-vars */
import { ComponentProps, ReactNode, forwardRef, useMemo } from 'react'
import { IonicCloseOutline } from 'maeng-icons'
import classNames from 'classnames'
import { Override } from 'maeng-type/Common'

export type ModalProps = Override<
  ComponentProps<'dialog'>,
  {
    id?: string
    title?: ReactNode
    closeButton?: boolean | ReactNode
    content?: ReactNode
    actions?: {
      cancel?:
        | (() => void)
        | Override<
            ComponentProps<'button'>,
            {
              children?: never
              label: ReactNode
            }
          >
      confirm?:
        | (() => void)
        | Override<
            ComponentProps<'button'>,
            {
              children?: never
              label: ReactNode
            }
          >
    }
    open?: boolean
    position?: 'top' | 'middle' | 'bottom'
  }
>

export const Modal = forwardRef<HTMLDialogElement, ModalProps>(
  (
    {
      id,
      title,
      closeButton,
      content,
      actions,
      open,
      position = 'middle',
      className: classProps = '',
      ...props
    },
    ref,
  ) => {
    const closeBtn = useMemo(() => {
      if (!closeButton) return null
      if (typeof closeButton === 'boolean')
        return (
          <button className="btn btn-sm btn-circle btn-ghost absolute right-4 top-4">
            <IonicCloseOutline />
          </button>
        )
      return closeButton
    }, [closeButton])

    const confirmBtn = useMemo(() => {
      if (!actions?.confirm) return null
      if (typeof actions.confirm === 'function')
        return (
          <button className="btn btn-primary" onClick={actions.confirm}>
            Confirm
          </button>
        )

      const {
        className: confirmClassName = '',
        label: confirmLabel,
        ...confirmProps
      } = actions.confirm
      return (
        <button
          {...confirmProps}
          className={classNames({
            'btn btn-primary': true,
            [confirmClassName]: !!confirmClassName,
          })}
        >
          {confirmLabel}
        </button>
      )
    }, [actions?.confirm])

    const cancelBtn = useMemo(() => {
      if (!actions?.cancel) return null
      if (typeof actions.cancel === 'function')
        return (
          <button className="btn btn-neutral" onClick={actions.cancel}>
            Cancel
          </button>
        )
      const { className: cancelClassName = '', label: cancelLabel, ...cancelProps } = actions.cancel
      return (
        <button
          {...cancelProps}
          className={classNames({
            'btn btn-neutral': true,
            [cancelClassName]: !!cancelClassName,
          })}
        >
          {cancelLabel}
        </button>
      )
    }, [actions?.cancel])

    const className = useMemo(() => {
      const status = {
        'modal-open': open,
      }
      const posistions = {
        'modal-top': position === 'top',
        'modal-middle': position === 'middle',
        'modal-bottom': position === 'bottom',
      }

      return classNames({
        modal: true,
        ...status,
        ...posistions,
        [classProps]: true,
      })
    }, [classProps, open, position])

    return (
      <dialog ref={ref} id={id} className={className} {...props}>
        <div className="modal-box">
          <form method="dialog">{closeBtn}</form>
          {title}
          {content}
          {actions && (
            <div className="modal-action">
              <form method="dialog" className="flex gap-4">
                {cancelBtn}
                {confirmBtn}
              </form>
            </div>
          )}
        </div>
        <form method="dialog" className="modal-backdrop">
          <button>close</button>
        </form>
      </dialog>
    )
  },
)

export default Modal
